import React, { useContext, useCallback } from 'react';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { useLocation } from '@thd-olt-component-react/router';
import PropTypes from 'prop-types';
import {
  shape, string, useDataModel, arrayOf, number, bool, params
} from '@thd-nucleus/data-sources';
import { ResultsSortBy } from '../components/ResultsSortBy';
import { ProductResultsSearchMethodsPLP } from '../utils/product-results-search-methods-PLP';
import { shouldDisplayResultsAppliedOuter, shouldDisplayResultsAppliedInner } from '../utils/searchHelpers';

export const ResultsSortByComposable = ({ renderingConditions = {} }) => {
  const { onSortChangePLP } = ProductResultsSearchMethodsPLP();
  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const isGetItFastOnPlp = useConfigService('fs-prop:isGetItFastOnPlp');
  const { customContext } = useContext(ExperienceContext);
  const { variables = {}, varnish } = customContext || {};
  const store = useStore({ varnish, online: false });
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;

  const defaultOptions = {
    variables: {
      ...variables,
      storeId: variables?.storeId || store?.storeId,
    },
    ssr: true,
    skip: !variables?.keyword && !variables?.navParam
  };
  const { data = {} } = useDataModel('searchModel', defaultOptions);
  const { metadata } = data?.searchModel || {};
  const { contentType } = metadata || {};
  const isCategory = contentType === 'categorypage';

  const handleSortChange = useCallback((sortParams) => {
    onSortChangePLP({ ...sortParams, path });
  }, [path]);
  if (isCategory) return null;

  const getConditionValue = (conditionName) => {
    switch (conditionName) {
    case 'shouldDisplayResultsAppliedOuter':
      return shouldDisplayResultsAppliedOuter(customContext);
    case 'shouldDisplayResultsAppliedInner':
      return shouldDisplayResultsAppliedInner(customContext);
    case 'viewPrimaryFilters':
      return viewPrimaryFilters;
    case 'isGetItFastOnPlp':
      return isGetItFastOnPlp;
    default:
      return true;
    }
  };

  const hasRenderingConditions = Object.keys(renderingConditions).length > 0;

  if (hasRenderingConditions) {
    const shouldRender = Object.entries(renderingConditions).every(([conditionName, expectedValue]) => {
      const actualValue = getConditionValue(conditionName);
      return actualValue === expectedValue;
    });
    if (!shouldRender) return null;
  }

  return (
    <ResultsSortBy
      onSortChange={handleSortChange}
      searchModelData={data}
    />
  );
};

ResultsSortByComposable.displayName = 'ResultsSortByComposable';

ResultsSortByComposable.defaultProps = {
  renderingConditions: {},
};

ResultsSortByComposable.propTypes = {
  renderingConditions: PropTypes.shape({
    shouldDisplayResultsAppliedOuter: PropTypes.bool,
    shouldDisplayResultsAppliedInner: PropTypes.bool,
    viewPrimaryFilters: PropTypes.bool,
    isGetItFastOnPlp: PropTypes.bool,
  })
};

// Define data model with key matching the queried model name 'searchModel'
ResultsSortByComposable.dataModel = {
  searchModel: params({ keyword: string(), navParam: string(), storeId: string() }).shape({
    metadata: shape({
      contentType: string(),
      h1Tag: string()
    }),
    searchReport: shape({
      startIndex: number(),
      pageSize: number(),
      keyword: string(),
      sortBy: string(),
      sortOrder: string()
    }),
    products: arrayOf(shape({
      identifiers: shape({
        productType: string()
      })
    }))
  })
};